/**
 * The feature-flag store: per-device local flags (localStorage-backed) and
 * the last-known household-wide flag snapshot. Framework- and network-free on
 * purpose, so code that is not a component and must not pull in the API,
 * router or live-socket modules can still read a flag's effective value.
 * src/features.ts builds the reactive (preact hook) surface most of the app
 * uses on top of this; src/audio.ts (not a component, and covered by a Node
 * test) reads `isFeatureEnabled` straight from here instead.
 */
import { FLAG_KEYS } from "../shared/flags.ts";
import { DEFAULT_SHIRT, type Shirt } from "../shared/shirts.ts";
import type { FlagKey, Flags } from "../shared/types";

interface LocalFlagStore {
  storageKey: string;
  event: string;
  get: () => boolean;
  set: (value: boolean) => void;
  /** Re-reads localStorage into the cached value; used when a `storage` event fires (another tab). */
  syncFromStorage: () => void;
}

function createLocalFlag(storageKey: string, event: string): LocalFlagStore {
  function readPreference(): boolean {
    try {
      return localStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  }
  // Presentation only. Calculations, ledger writes and saved job sizes stay active.
  let enabled = readPreference();
  return {
    storageKey,
    event,
    get: () => enabled,
    set(value: boolean) {
      enabled = value;
      try {
        localStorage.setItem(storageKey, value ? "1" : "0");
      } catch {
        /* The toggle still works for this session if storage is unavailable. */
      }
      window.dispatchEvent(new Event(event));
    },
    syncFromStorage() {
      enabled = readPreference();
    },
  };
}

/** Per-flag local store, keyed by FlagKey; features.ts's useLocalFlag hook wraps these reactively. */
export const LOCAL_FLAGS: Record<FlagKey, LocalFlagStore> = {
  bountyUI: createLocalFlag("op.bountyUI", "op:bounty-ui"),
  notesUI: createLocalFlag("op.notesUI", "op:notes-ui"),
  economyUI: createLocalFlag("op.economyUI", "op:economy-ui"),
  focusUI: createLocalFlag("op.focusUI", "op:focus-ui"),
  quickUI: createLocalFlag("op.quickUI", "op:quick-ui"),
  originalAssets: createLocalFlag("op.originalAssets", "op:original-assets"),
  livePeon: createLocalFlag("op.livePeon", "op:live-peon"),
  upgradesUI: createLocalFlag("op.upgradesUI", "op:upgrades-ui"),
  passkeys: createLocalFlag("op.passkeys", "op:passkeys"),
  standings: createLocalFlag("op.standings", "op:standings"),
  recapUI: createLocalFlag("op.recapUI", "op:recap-ui"),
  timersUI: createLocalFlag("op.timersUI", "op:timers-ui"),
  shopUI: createLocalFlag("op.shopUI", "op:shop-ui"),
  achievementsUI: createLocalFlag("op.achievementsUI", "op:achievements-ui"),
  ranksUI: createLocalFlag("op.ranksUI", "op:ranks-ui"),
  purseUI: createLocalFlag("op.purseUI", "op:purse-ui"),
  claimUI: createLocalFlag("op.claimUI", "op:claim-ui"),
  nudgeUI: createLocalFlag("op.nudgeUI", "op:nudge-ui"),
  weeklyUI: createLocalFlag("op.weeklyUI", "op:weekly-ui"),
  wallpaperUI: createLocalFlag("op.wallpaperUI", "op:wallpaper-ui"),
  streakUI: createLocalFlag("op.streakUI", "op:streak-ui"),
  rushPickUI: createLocalFlag("op.rushPickUI", "op:rush-pick-ui"),
  auctionUI: createLocalFlag("op.auctionUI", "op:auction-ui"),
  wagerUI: createLocalFlag("op.wagerUI", "op:wager-ui"),
  galleryUI: createLocalFlag("op.galleryUI", "op:gallery-ui"),
  compareUI: createLocalFlag("op.compareUI", "op:compare-ui"),
  peonMoodsUI: createLocalFlag("op.peonMoodsUI", "op:peon-moods-ui"),
  rouletteUI: createLocalFlag("op.rouletteUI", "op:roulette-ui"),
  yoursUI: createLocalFlag("op.yoursUI", "op:yours-ui"),
  giveCreditUI: createLocalFlag("op.giveCreditUI", "op:give-credit-ui"),
  trinketsUI: createLocalFlag("op.trinketsUI", "op:trinkets-ui"),
  lateRushUI: createLocalFlag("op.lateRushUI", "op:late-rush-ui"),
  heartsUI: createLocalFlag("op.heartsUI", "op:hearts-ui"),
  mentionsUI: createLocalFlag("op.mentionsUI", "op:mentions-ui"),
  speedrunUI: createLocalFlag("op.speedrunUI", "op:speedrun-ui"),
  photoNotesUI: createLocalFlag("op.photoNotesUI", "op:photo-notes-ui"),
  slopTallyUI: createLocalFlag("op.slopTallyUI", "op:slop-tally-ui"),
  feedbackUI: createLocalFlag("op.feedbackUI", "op:feedback-ui"),
  yoursPushUI: createLocalFlag("op.yoursPushUI", "op:yours-push-ui"),
  cheersUI: createLocalFlag("op.cheersUI", "op:cheers-ui"),
  helloUI: createLocalFlag("op.helloUI", "op:hello-ui"),
  houseGoalUI: createLocalFlag("op.houseGoalUI", "op:house-goal-ui"),
  themeWeekUI: createLocalFlag("op.themeWeekUI", "op:theme-week-ui"),
  shareCreditUI: createLocalFlag("op.shareCreditUI", "op:share-credit-ui"),
  projectsUI: createLocalFlag("op.projectsUI", "op:projects-ui"),
};

/** Non-hook read of this device's local flag state. */
export function getLocalFlag(key: FlagKey): boolean {
  return LOCAL_FLAGS[key].get();
}
export function setLocalFlag(key: FlagKey, value: boolean): void {
  LOCAL_FLAGS[key].set(value);
}

// ---------- Household (server-side) flags ----------
//
// The "Everyone" half of a feature switch: stored in the `meta` table
// (server/db.ts getFlags/setFlag) and shared by every device. features.ts
// fetches it lazily (not on module load, since the client may not be logged
// in yet) and keeps it live via useHouseholdFlagsSync.

export const HOUSEHOLD_EVENT = "op:flags";
export const HOUSEHOLD_LOADED_EVENT = "op:flags-loaded";
/** How long a board-supplied flags read counts as "fresh enough" to skip a redundant /api/flags call. */
export const RECENT_APPLY_MS = 5_000;

const ALL_OFF = (): Flags => Object.fromEntries(FLAG_KEYS.map((k) => [k, false])) as Flags;

// ---------- The last-known household flags, per household ----------
//
// On a cold load the board's summary brings the flags a moment after the first paint, and the
// Upgrades tab, Pin a note and the live peon popped in late, shifting the tab bar. The last flags
// each household sent are kept in localStorage under its id, and the page renders from them until
// the summary (or /api/flags) replaces them. `householdLoaded` still waits for a real read, so a
// stale upgrade link is only bounced on the server's word. This is the household's switches only:
// the gate never reads it (a passkey must open the door by itself, docs/members.md).

/** The household the device last had active (app.tsx sets it from AuthState), so a cold load knows whose cache to read. */
const LAST_HOUSEHOLD_KEY = "op.lastHousehold";
const CACHE_PREFIX = "op.householdFlags.";

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode etc.: the page still works, it just pops in as before */
  }
}

/** A cached snapshot, every key a boolean (a key the cache predates is off), or null when there is none or it is unreadable. */
export function parseCachedFlags(raw: string | null): Flags | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== "object") return null;
    const out = ALL_OFF();
    for (const k of FLAG_KEYS) out[k] = parsed[k] === true;
    return out;
  } catch {
    return null;
  }
}

function cachedFlagsFor(householdId: string | null): Flags | null {
  return householdId ? parseCachedFlags(readStorage(CACHE_PREFIX + householdId)) : null;
}

let cacheHousehold: string | null = readStorage(LAST_HOUSEHOLD_KEY);
let household: Flags = cachedFlagsFor(cacheHousehold) ?? ALL_OFF();
/** True once the household flags have been fetched successfully at least once this page load. */
let householdLoaded = false;

/**
 * The household the flags belong to (AuthState.household.id, set by app.tsx once auth is known).
 * Another household than the one the cache was read for swaps in its own cache, or all off.
 */
export function setFlagsHousehold(id: string | null): void {
  if (id === cacheHousehold) return;
  cacheHousehold = id;
  if (id) writeStorage(LAST_HOUSEHOLD_KEY, id);
  if (householdLoaded) {
    // The server's flags already came in this page load, for this household: keep them, cache them.
    if (id) writeStorage(CACHE_PREFIX + id, JSON.stringify(household));
    return;
  }
  const next = cachedFlagsFor(id) ?? ALL_OFF();
  if (!sameFlags(household, next)) {
    household = next;
    window.dispatchEvent(new Event(HOUSEHOLD_EVENT));
  }
}
/** When applyHouseholdFlags last ran (0 = never); see RECENT_APPLY_MS. */
let lastAppliedAt = 0;

function sameFlags(a: Flags, b: Flags): boolean {
  return FLAG_KEYS.every((k) => a[k] === b[k]);
}

/** Applies a freshly-read household snapshot, dispatching HOUSEHOLD_EVENT only when it actually changed. */
export function applyHousehold(next: Flags): void {
  if (cacheHousehold) writeStorage(CACHE_PREFIX + cacheHousehold, JSON.stringify(next));
  if (!sameFlags(household, next)) {
    household = next;
    window.dispatchEvent(new Event(HOUSEHOLD_EVENT));
  }
  if (!householdLoaded) {
    householdLoaded = true;
    window.dispatchEvent(new Event(HOUSEHOLD_LOADED_EVENT));
  }
}

/**
 * Feeds flags the board already fetched as part of its one request (api.board.summary) into the
 * same store loadHouseholdFlags fills, so a screen showing the board does not also need its own
 * /api/flags call on every live change. See features.ts's useHouseholdFlagsSync.
 */
export function applyHouseholdFlags(flags: Flags): void {
  applyHousehold(flags);
  lastAppliedAt = Date.now();
}

export function getLastAppliedAt(): number {
  return lastAppliedAt;
}
export function isHouseholdLoaded(): boolean {
  return householdLoaded;
}
export function getHouseholdFlag(key: FlagKey): boolean {
  return household[key];
}
/** The household object as it currently stands (see applyHousehold: a new object only on change). */
export function getHousehold(): Flags {
  return household;
}

/**
 * Non-hook snapshot of every flag's effective state (this device's switch OR
 * the household's). Used by app.tsx to build the board's prefetch opts
 * before any flag hook has mounted; the household half may still be the
 * all-off default this early in a page load, which is fine, since that's
 * exactly what the Board's own first render would see too.
 */
export function currentFlags(): Flags {
  const out = {} as Flags;
  for (const key of FLAG_KEYS) out[key] = getLocalFlag(key) || household[key];
  return out;
}

/**
 * Non-hook effective read of one flag: this device's switch OR the
 * household's, re-checked on every call rather than cached. For code that is
 * not a component and must see a live change without re-rendering (src/audio.ts,
 * which reads this at the moment a line is about to play).
 */
export function isFeatureEnabled(key: FlagKey): boolean {
  return getLocalFlag(key) || household[key];
}

/**
 * The crowned portrait's URL: the pixel-art mascot in the Peon of the Month's shirt
 * (scripts/make-shirts.mjs, owner 2026-09-24), or the Warcraft II portrait it replaces, which has no
 * shirts and is the same for everyone.
 */
export function crownedPortraitUrl(originalAssets: boolean, shirt: Shirt = DEFAULT_SHIRT): string {
  if (!originalAssets) return "/icons/peon-crowned.png";
  return shirt === DEFAULT_SHIRT ? "/ui/peon-crowned@3x.png" : `/ui/peon-crowned-${shirt}@3x.png`;
}
